import React, { useMemo } from "react";
import { formatMoney } from "../../../../features/utils/formatMoney";
import {
  getChangeAdminGuidance,
  getChangeKindLabel,
  isOpenTicketChange,
} from "../../../../data/flight/order/getChangeAdminGuidance";
import {
  getFlightBookingFromChange,
  getRequestedDateChangeSummary,
} from "../../../../data/flight/order/getRequestedDateChange";

function Row({ label, value }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="flex justify-between gap-3 py-1 text-sm border-b border-[#F1F5F9] last:border-0">
      <span className="text-[#6D7C94] shrink-0">{label}</span>
      <span className="text-[#0F172A] text-right font-medium break-words">
        {value}
      </span>
    </div>
  );
}

function money(n) {
  if (n == null || n === "") return null;
  return formatMoney(Number(n) || 0);
}

/**
 * Task 9 — manual processing audit snapshot from fields already on the order.
 */
export default function ChangeManualAuditCard({ data }) {
  const guidance = getChangeAdminGuidance(data);
  const cachedAdmin = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(`changeQuoteAdmin:${data?._id}`) || "null",
      );
    } catch (_) {
      return null;
    }
  }, [data?._id]);

  if (!data || isOpenTicketChange(data)) return null;

  const booking = getFlightBookingFromChange(data) || {};
  const currency =
    data?.currency || booking?.currency || data?.flightBooking?.currency || "";

  const assignee = data?.tasks?.[0]?.assignee;
  const opsUser =
    data?.approvedBy ||
    data?.processedBy ||
    data?.manualProcessedBy ||
    (assignee
      ? `${assignee.firstName || ""} ${assignee.lastName || ""}`.trim()
      : null) ||
    data?.updatedBy?.name ||
    data?.updatedBy;

  const timestampRaw =
    data?.approvedAt ||
    data?.manualProcessedAt ||
    data?.updatedAt ||
    data?.quotedAt ||
    cachedAdmin?.savedAt;
  const timestamp = timestampRaw
    ? new Date(timestampRaw).toLocaleString()
    : null;

  const originalItinerary = (booking?.destinations || [])
    .map(
      (d) =>
        `${d?.from || "?"} → ${d?.to || "?"} · ${d?.departureDate || ""}`,
    )
    .filter(Boolean)
    .join("; ");

  const requestedItinerary = getRequestedDateChangeSummary(data) || "—";

  const pb = data?.priceBreakdown?.[0] || {};
  const rows = [
    { label: "Ops user", value: opsUser || "—" },
    { label: "Timestamp", value: timestamp || "—" },
    {
      label: "Reason for manual",
      value:
        data?.manualInterventionReason ||
        data?.manualReason ||
        (guidance.stage === "needs_quote" || guidance.stage === "needs_review"
          ? "Automatic pricing unavailable or failed"
          : null),
    },
    {
      label: "Supplier",
      value: data?.supplier || booking?.supplier || "—",
    },
    {
      label: "Ticket number",
      value:
        data?.newTicketNumber ||
        data?.ticketNumber ||
        booking?.ticketNumber ||
        booking?.tickets?.[0]?.number ||
        "—",
    },
    { label: "Original itinerary", value: originalItinerary || "—" },
    { label: "Requested itinerary", value: requestedItinerary },
    {
      label: "Supplier fare difference",
      value: money(
        data?.supplierChangePrice ??
          pb?.airlineFareDifference ??
          cachedAdmin?.airlineFareDifference,
      ),
    },
    {
      label: "Intraverse markup",
      value: money(
        data?.fareDifferenceMarkup ??
          pb?.fareDifferenceMarkup ??
          cachedAdmin?.fareDifferenceMarkup,
      ),
    },
    {
      label: "Selling fare difference",
      value: money(
        data?.ticketDifference ??
          pb?.ticketDifference ??
          cachedAdmin?.sellingFareDifference,
      ),
    },
    {
      label: "Penalty",
      value: money(data?.penaltyFee ?? pb?.penaltyFee),
    },
    {
      label: "Taxes",
      value: money(
        data?.taxDifference ??
          pb?.taxDifference ??
          cachedAdmin?.taxDifference,
      ),
    },
    {
      label: "Supplier fee",
      value: money(
        data?.supplierFee ?? pb?.supplierFee ?? cachedAdmin?.supplierFee,
      ),
    },
    {
      label: "Service fee",
      value: money(
        data?.intraverseServiceFee ||
          data?.changeFee ||
          cachedAdmin?.intraverseServiceFee,
      ),
    },
    {
      label: "Supplier response",
      value:
        data?.supplierResponse ||
        data?.lastError ||
        data?.rejectReason ||
        null,
    },
    {
      label: "Exchange reference",
      value:
        data?.exchangeReference ||
        data?.reissueReference ||
        data?.supplierConfirmation ||
        null,
    },
    {
      label: "Notes",
      value: data?.remark || data?.notes || data?.manualNotes || null,
    },
    currency ? { label: "Currency", value: currency } : null,
  ].filter(Boolean);

  return (
    <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-900">
          Manual processing audit
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
          {getChangeKindLabel(data)}
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Snapshot of what Ops entered and what the platform stored. Original
        ticket stays for audit.
      </p>
      <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-1">
        {rows.map((row) => (
          <Row key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
    </div>
  );
}
